import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import translations from './translations';

const LanguageContext = createContext();

// Detect browser language and map to supported languages
const detectBrowserLanguage = () => {
  const browserLang = navigator.language || navigator.userLanguage || 'en';
  
  // Map browser language to supported languages (only English and Portuguese)
  if (browserLang.startsWith('pt')) {
    return 'pt-BR';
  } else {
    return 'en';
  }
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    // First check localStorage for saved preference
    const saved = localStorage.getItem('language');
    if (saved && (saved === 'en' || saved === 'pt-BR')) return saved;
    
    // Otherwise detect from browser
    return detectBrowserLanguage();
  });

  useEffect(() => {
    localStorage.setItem('language', language);
    // Update HTML lang attribute for accessibility
    document.documentElement.lang = language;
  }, [language]);

  const t = useCallback((key) => {
    const keys = key.split('.');
    let value = translations[language];
    
    for (const k of keys) {
      if (value && value[k]) {
        value = value[k];
      } else {
        // Fallback to English
        value = translations['en'];
        for (const k2 of keys) {
          if (value && value[k2]) {
            value = value[k2];
          } else {
            return key;
          }
        }
        break;
      }
    }
    
    return typeof value === 'string' ? value : key;
  }, [language]);

  const toggleLanguage = useCallback(() => {
    setLanguage(prev => prev === 'en' ? 'pt-BR' : 'en');
  }, []);

  // Get available languages (only English and Portuguese)
  const availableLanguages = useMemo(() => [
    { code: 'en', name: 'English', flag: '🇺🇸' },
    { code: 'pt-BR', name: 'Português', flag: '🇧🇷' }
  ], []);

  // Memoize the context value to prevent unnecessary re-renders
  const value = useMemo(() => ({ 
    language, 
    setLanguage, 
    toggleLanguage, 
    t,
    availableLanguages,
    detectBrowserLanguage
  }), [language, toggleLanguage, t, availableLanguages]);

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export default LanguageProvider;
